import { useMemo, type ComponentPropsWithoutRef } from 'react';
import { useListProgressObjectsQuery, useListPtoProjectsQuery } from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Select } from '@shared/ui/Input';

export interface PayObject {
  id: string;
  name: string;
}

/**
 * Объекты, на которые можно отнести счёт: проекты ПТО (есть доступ к ПТО),
 * иначе объекты Руководителя. Те же запросы, что у выбора объекта в шапке
 * ОСНОВЫ, — список берётся из кеша. Нет доступа ни к тому, ни к другому —
 * пусто: поле «Объект» не показываем, оно необязательное.
 */
export function usePayObjects(): PayObject[] {
  const pto = useAgentPermission('pto');
  const progress = useAgentPermission('progress');
  const ptoQ = useListPtoProjectsQuery(undefined, { skip: !pto.can_read });
  const progressQ = useListProgressObjectsQuery(undefined, {
    skip: pto.can_read || !progress.can_read,
  });
  return useMemo(() => {
    const list = ptoQ.data?.items ?? progressQ.data ?? [];
    return list
      .map((o) => ({ id: o.id, name: o.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [ptoQ.data, progressQ.data]);
}

/** Остальные свойства (id, aria-*, onBlur) уходят в сам `<select>`. */
interface Props extends Omit<ComponentPropsWithoutRef<'select'>, 'value' | 'onChange'> {
  value: string;
  objects: PayObject[];
  onChange: (id: string) => void;
}

export function ObjectSelect({ value, objects, onChange, ...rest }: Props) {
  return (
    <Select {...rest} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— не относится к объекту —</option>
      {objects.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </Select>
  );
}
